// SaveVersionNameDialog - りれきの かくにんの「名前をつけて保存」で、記録につける名前を入れる。
// はじめは「セーブポイント_YYYYMMDD_HHmmss」を入れておく（開くたびにその時刻で入れ直す）。
import { useState, useEffect, FormEvent, KeyboardEvent } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { useScale, btnDelay, usePlaySound } from "common/utils";

/** 名前の最大文字数（API の SAVE_NAME_MAX と同じ） */
const SAVE_NAME_MAX = 30;

export const SaveVersionNameDialog = ({
  visible,
  close,
  save,
}: {
  visible: boolean;
  close: () => void;
  save: (name: string) => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const [name, setName] = useState("");

  useEffect(() => {
    if (!visible) return;
    setName(
      I18n.t("MSG_CREATOR_HISTORY_SAVE_DEFAULT_NAME", {
        time: dayjs().format("YYYYMMDD_HHmmss"),
      }),
    );
  }, [visible]);

  const length = [...name.trim()].length;
  const valid = length > 0 && length <= SAVE_NAME_MAX;

  const handleClose = () => {
    play();
    btnDelay(() => close());
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!valid) return;
    play();
    btnDelay(() => save(name.trim()));
  };

  // 日本語入力の変換を確定する Enter では送らない
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && event.nativeEvent.isComposing) event.preventDefault();
  };

  return (
    <Dialog
      open={visible}
      maxWidth={false}
      sx={{ zIndex: 20020 }}
      TransitionComponent={FadeTransition}
      componentsProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center relative" style={{ transform: `scale(${scale})` }}>
        <div
          className="flex-col-el w-[510px] border-solid border-white border-[8px] rounded-lg bg-white box-border"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <CloseBtn close={handleClose} />
          {/* 並びと余白は「アプリをつくる」の名前入力（CreateProjectModal）と同じにする */}
          <div className="flex-col-el justify-start w-full p-4 rounded bg-beige-60 box-border">
            <div className="flex-row-el justify-start py-4">
              <p className="type-text-left type-md text-textcolor-black">
                {I18n.t("MSG_CREATOR_HISTORY_SAVE_BTN")}
              </p>
            </div>

            <div className="flex-col-el items-start py-4 gap-3">
              <p className="type-text-left type-sm text-textcolor-black">
                {I18n.t("MSG_CREATOR_HISTORY_SAVE_NAME_DESCRIPTION")}
              </p>

              <form onSubmit={handleSubmit} className="flex-col-el w-full gap-4">
                <div className="flex-col-el">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="input-modal type-text-left type-md text-textcolor-black"
                  />
                  <p
                    className={`type-text-left type-sm text-danger h-6 ${valid ? "invisible" : ""}`}
                  >
                    {I18n.t("MSG_BLOCKLY_INPUT_LENGTH_LIMIT", { limit: SAVE_NAME_MAX })}
                  </p>
                </div>

                {/* alpha の余白は em 指定。type-inherit-font で文字が 24px になると大きくなりすぎるので、
                    「アプリをつくる」の「けってい」と同じ大きさに余白を合わせる */}
                <div className="flex-row-el flex-center alpha">
                  <button
                    type="submit"
                    disabled={!valid}
                    className="type-inherit-font btn btn-primary !px-6 !py-3"
                  >
                    <div className="flex-row-center gap-1">
                      <span className="material-symbols-outlined type-title text-white">check</span>
                      <span className="type-text type-body text-white">
                        {I18n.t("MSG_CONFIRM_BTN")}
                      </span>
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
